
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, User, FileText, DollarSign, Users } from "lucide-react";

interface PermissionSettings {
  canUpdateProfile: boolean;
  canViewInvoices: boolean;
  canRegisterStudents: boolean;
  canUploadDocuments: boolean;
}
interface Props {
  permissionSettings: PermissionSettings;
  onPermissionToggle: (field: keyof PermissionSettings) => void;
  isSaving: boolean;
}
const ParentPortalPermissionsTab: React.FC<Props> = ({
  permissionSettings,
  onPermissionToggle,
  isSaving,
}) => {
  const permissions = [
    {
      key: 'canUpdateProfile' as keyof PermissionSettings,
      icon: User,
      title: 'Update Profile Information',
      description: 'Allow parents to edit their personal information and contact details',
      checked: permissionSettings.canUpdateProfile
    },
    {
      key: 'canViewInvoices' as keyof PermissionSettings,
      icon: DollarSign,
      title: 'View Invoices and Payments',
      description: 'Grant access to billing history, invoices, and payment information',
      checked: permissionSettings.canViewInvoices
    },
    {
      key: 'canRegisterStudents' as keyof PermissionSettings,
      icon: Users,
      title: 'Register Students for Classes',
      description: 'Enable parents to enroll their children in available classes and programs',
      checked: permissionSettings.canRegisterStudents
    },
    {
      key: 'canUploadDocuments' as keyof PermissionSettings,
      icon: FileText,
      title: 'Upload and Manage Documents',
      description: 'Allow document uploads such as waivers, medical forms, and other required paperwork',
      checked: permissionSettings.canUploadDocuments
    }
  ];

  return (
    <div className="max-w-5xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Parent Permissions
          </CardTitle>
          <CardDescription>
            Configure what actions parents can perform in the parent portal. These settings apply to all families in your organization.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {permissions.map((permission) => {
              const IconComponent = permission.icon;
              return (
                <div
                  key={permission.key}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      <IconComponent className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={permission.key} className="font-medium cursor-pointer">
                        {permission.title}
                      </Label>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {permission.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={permission.key}
                    checked={permission.checked}
                    onCheckedChange={() => onPermissionToggle(permission.key)}
                    disabled={isSaving}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Security Notice</h4>
              <p className="text-sm text-amber-700 mt-1">
                Changes to parent permissions take effect immediately. Consider the privacy and security implications 
                before enabling sensitive features like invoice viewing or document management.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentPortalPermissionsTab;
